/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { FocusZone } from '@fluentui/react/lib/FocusZone';
import { ISelection, SelectionZone } from '@fluentui/react/lib/Selection';
import { getFirstFocusable } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { getStylist } from 'src/core/themes/luisStylist';
import { LuisKeyCodes } from 'src/core/utils/keyCodes';

export type TagContainerProps<T> = React.PropsWithChildren<{
    /**
     * The selection object used to govern the
     * selection zone.
     */
    selection?: ISelection;

    /**
     * A callback when selected items are removed.
     */
    onRemoveItems?: (items: T[]) => void;

    /**
     * A callback when esc is pressed.
     */
    onEscPress?: () => void;
}>;

const { styleDiv } = getStylist('TagContainer');
const ChildrenContainer = styleDiv('Container', { display: 'flex', flexFlow: 'row wrap', alignItems: 'center' });

/**
 * Represents a generic container for tags. Mostly used with
 * phrase list and list entity suggestions.
 *
 * Requires that tag children contain a data-is-tag attribute for
 * accessibility to work correctly.
 *
 * @param props The container input.
 */
export const TagContainer = <T extends object>(props: TagContainerProps<T>) => {
    const { selection, onRemoveItems, onEscPress } = props;
    const focusZoneRef = React.useRef<any>();
    const tagContainerRef = React.useRef<HTMLDivElement>();
    const hasTagChildren = tagContainerRef.current?.querySelector('[role="listitem"]') !== null;

    /**
     * Checks if selected items were deleted via the delete or backspace
     * buttons. If that is the case, the remove items callback is issued.
     *
     * @param event The key up event.
     */
    const onKeyUp = (event: React.KeyboardEvent<HTMLDivElement>): void => {
        if (event.key === LuisKeyCodes.Escape && onEscPress) {
            onEscPress();
            return;
        }

        if (!selection) {
            return;
        }

        if (event.key !== 'Delete' && event.key !== 'Backspace') {
            return;
        }

        if (!selection.getSelectedCount()) {
            return;
        }

        if (onRemoveItems) {
            onRemoveItems(selection.getSelection() as T[]);
        }
    };

    const tagsContainer = () => (
        <div ref={tagContainerRef}>
            <ChildrenContainer aria-label="list" role={hasTagChildren ? 'list' : undefined}>
                {props.children}
            </ChildrenContainer>
        </div>
    );

    return (
        <FocusZone
            onKeyUp={onKeyUp}
            onBlur={onEscPress}
            data-is-focusable="true"
            componentRef={focusZoneRef}
            onFocusCapture={_e => {
                if (focusZoneRef.current?._root.current === _e.target) {
                    getFirstFocusable(tagContainerRef.current, tagContainerRef.current, true)?.focus();
                }
            }}>
            {selection ? <SelectionZone selection={selection}>{tagsContainer()}</SelectionZone> : tagsContainer()}
        </FocusZone>
    );
};
